import { useRef, useState } from 'react';
import {
    Box,
    IconButton,
    Step,
    StepLabel,
    Stepper,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import MDButton from '@components/MDButton';
import { MessageField, VariableChips, insertAtCursor } from '@components/MessageField';
import { CurrencyInput } from '@components/CurrencyInput';
import { StandaloneAutoCompleteSelect } from '@components/StandaloneAutoCompleteSelect';
import { LabelledSelect } from '@components/StandaloneAutoCompleteSelect/labelled-select';
import { SOURCES } from '@layouts/pages/CRM/kanban-board/formatters/lead-source-label';
import { useMaterialUIController } from 'context';
import { useTranslation } from 'react-i18next';
import { useAlert } from '@components/showAlert/useAlertHook';
import { apiErrorMessage } from '@functions/api-error-message';
import { getCrmColors } from '@layouts/pages/CRM/kanban-board/crm-theme';
import { BoardColumnWithLeads } from '@layouts/pages/CRM/kanban-board/services/responses/board-column.response';
import {
    AutomationService,
    EStageAction,
    EStageTrigger,
    TRIGGERS_WITH_DUE_DATE,
    StageAutomation,
} from '../services/automation.service';
import { ruleSentence } from '../automation-sentence';
import { RulePanel } from './rule-list';

export const ACTION_ICON: Record<EStageAction, JSX.Element> = {
    [EStageAction.SendMessage]: <ChatBubbleOutlineIcon sx={{ fontSize: 18 }} />,
    [EStageAction.CreateTask]: <EventAvailableIcon sx={{ fontSize: 18 }} />,
    [EStageAction.Assign]: <PersonAddAltIcon sx={{ fontSize: 18 }} />,
    [EStageAction.MoveToColumn]: <ArrowForwardIcon sx={{ fontSize: 18 }} />,
    [EStageAction.SetStatus]: <ThermostatIcon sx={{ fontSize: 18 }} />,
};

interface Props {
    rule: StageAutomation | null;
    boardColumnId: string;
    columns: BoardColumnWithLeads[];
    users: { id: string; name: string }[];
    statuses: { id: string; name: string }[];
    /// Descarta o que foi digitado.
    onCancel: () => void;
    onSaved: (saved: StageAutomation) => void;
    /// Só a regra que já existe liga, desliga e sai.
    onToggle?: () => void;
    onDelete?: () => void;
}

/// Cria e edita regra de coluna ao lado da lista; a frase no topo acompanha o que é escolhido.
export const StageRuleForm = ({
    rule,
    boardColumnId,
    columns,
    users,
    statuses,
    onCancel,
    onSaved,
    onToggle,
    onDelete,
}: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { showAlert } = useAlert();

    const [name, setName] = useState(rule?.name ?? '');
    const [trigger, setTrigger] = useState<EStageTrigger>(rule?.trigger ?? EStageTrigger.OnEnter);
    const [days, setDays] = useState(rule?.idleDays || 3);
    const [action, setAction] = useState<EStageAction>(rule?.action ?? EStageAction.SendMessage);
    const [message, setMessage] = useState(rule?.messageText ?? '');
    const [task, setTask] = useState(rule?.taskDescription ?? '');
    const [dueInDays, setDueInDays] = useState(rule?.taskDueInDays ?? 0);
    const [destination, setDestination] = useState(rule?.targetColumnId ?? '');
    const [assignee, setAssignee] = useState(rule?.assignUserId ?? '');
    const [status, setStatus] = useState(rule?.targetStatusId ?? '');
    const [hoursBefore, setHoursBefore] = useState(rule?.hoursBefore || 24);
    const [delayHours, setDelayHours] = useState(rule?.delayHours ?? 0);
    const [minValue, setMinValue] = useState<number | null>(rule?.conditionMinValue ?? null);
    const [source, setSource] = useState<number | ''>(rule?.conditionSource ?? '');
    const [saving, setSaving] = useState(false);

    /// 0 = quando (gatilho e filtros), 1 = então (ação, atraso e nome).
    const [step, setStep] = useState(0);
    const taskRef = useRef<HTMLInputElement | null>(null);

    /// Só após a primeira tentativa de salvar os campos ficam vermelhos.
    const [touched, setTouched] = useState(false);

    /// A coluna só é escolhida ao criar: a regra que já existe fica onde nasceu.
    const [column, setColumn] = useState(boardColumnId);

    const actions = [
        { value: EStageAction.SendMessage, label: t('automacoes.acaoMensagem') },
        { value: EStageAction.CreateTask, label: t('automacoes.acaoTarefa') },
        { value: EStageAction.Assign, label: t('automacoes.acaoAtribuir') },
        { value: EStageAction.MoveToColumn, label: t('automacoes.acaoMover') },
        { value: EStageAction.SetStatus, label: t('automacoes.acaoStatus') },
    ].map((option) => ({ ...option, icon: ACTION_ICON[option.value] }));

    /// Só os gatilhos de tempo pedem prazo; ganho e perda acontecem num instante.
    const needsDueDate = TRIGGERS_WITH_DUE_DATE.includes(trigger);

    const field = {
        '& .MuiOutlinedInput-root': {
            bgcolor: cc.surfaceBg,
            borderRadius: '8px',
            color: cc.textPrimary,
            '& fieldset': { borderColor: cc.border },
        },
        '& .MuiInputBase-input': { color: cc.textPrimary },
        '& .MuiFormLabel-root': { color: cc.textSecondary },
    };

    /// O que falta preencher, na ordem em que aparece na tela.
    const missing = () => {
        if (!name.trim()) return t('automacoes.informeONome');
        if (action === EStageAction.SendMessage && !message.trim())
            return t('automacoes.informeAMensagem');
        if (action === EStageAction.CreateTask && !task.trim())
            return t('automacoes.informeATarefa');

        return null;
    };

    const save = async () => {
        setTouched(true);

        // Barra aqui em vez de deixar o servidor recusar: o "faltou o nome" tardio não diz qual campo.
        const pending = missing();

        if (pending) {
            // Os obrigatórios estão todos na segunda etapa: o aviso precisa do campo à vista.
            setStep(1);
            showAlert(pending, 'warning', 3500);
            return;
        }

        setSaving(true);

        const body = {
            boardColumnId: column,
            name: name.trim(),
            enabled: rule?.enabled ?? true,
            trigger,
            idleDays: needsDueDate ? days : 0,
            action,
            messageText: action === EStageAction.SendMessage ? message : null,
            taskDescription: action === EStageAction.CreateTask ? task : null,
            taskDueInDays: action === EStageAction.CreateTask ? dueInDays : 0,
            targetColumnId: action === EStageAction.MoveToColumn ? destination || null : null,
            assignUserId: action === EStageAction.Assign ? assignee || null : null,
            targetStatusId: action === EStageAction.SetStatus ? status || null : null,
            hoursBefore: trigger === EStageTrigger.BeforeSchedule ? hoursBefore : 0,
            delayHours,
            conditionMinValue: minValue,
            conditionSource: source === '' ? null : Number(source),
        };

        try {
            const service = new AutomationService();
            const res = rule
                ? await service.updateStage(rule.id, body)
                : await service.createStage(body);

            showAlert(t(rule ? 'general.atualizacao' : 'general.cadastro'), 'success', 2500);
            onSaved(res.data.data);
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 4000);
        } finally {
            setSaving(false);
        }
    };

    const sentence = ruleSentence(
        t,
        {
            boardColumnId: column,
            trigger,
            idleDays: days,
            hoursBefore,
            action,
            messageText: message,
            taskDescription: task,
            taskDueInDays: dueInDays,
            targetColumnId: destination || null,
            assignUserId: assignee || null,
            targetStatusId: status || null,
            delayHours,
            conditionMinValue: minValue,
            conditionSource: source === '' ? null : Number(source),
        },
        { columns, users, statuses }
    );

    return (
        <RulePanel
            icon={ACTION_ICON[action]}
            title={rule ? rule.name : name.trim() || t('automacoes.novaRegra')}
            sentence={sentence}
            enabled={rule?.enabled}
            onToggle={onToggle}
            actions={
                onDelete && (
                    <Tooltip title={t('general.excluir')}>
                        <IconButton size="small" onClick={onDelete} sx={{ color: cc.textMuted }}>
                            <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                    </Tooltip>
                )
            }
            footer={
                <>
                    <MDButton
                        variant="gradient"
                        color="dark"
                        onClick={step === 0 ? onCancel : () => setStep(0)}
                    >
                        {step === 0 ? t('general.cancelar') : t('automacoes.voltar')}
                    </MDButton>
                    {step === 0 && (
                        <MDButton variant="gradient" color="info" onClick={() => setStep(1)}>
                            {t('automacoes.avancar')}
                        </MDButton>
                    )}
                    {/* Regra existente salva de qualquer etapa: mudar só o gatilho não pede passar pela segunda. */}
                    {(step === 1 || rule) && (
                        <MDButton
                            variant="gradient"
                            color="success"
                            onClick={save}
                            disabled={saving}
                        >
                            {t('general.salvar')}
                        </MDButton>
                    )}
                </>
            }
        >
            {/* Clicável: a primeira etapa não tem obrigatório, então pular não deixa nada pela metade. */}
            <Stepper activeStep={step} alternativeLabel>
                {[t('automacoes.quando'), t('automacoes.entao')].map((label, index) => (
                    <Step key={label} completed={index < step}>
                        <StepLabel onClick={() => setStep(index)} sx={{ cursor: 'pointer' }}>
                            {label}
                        </StepLabel>
                    </Step>
                ))}
            </Stepper>

            {step === 0 && (
                <>
                    {!rule && (
                        <Box sx={{ width: 240 }}>
                            <LabelledSelect cc={cc} label={t('automacoes.coluna')}>
                                <StandaloneAutoCompleteSelect
                                    options={columns.map((item) => ({
                                        value: item.id,
                                        label: item.name,
                                    }))}
                                    value={column}
                                    onChange={(value) => value && setColumn(value as string)}
                                    placeholder={t('automacoes.coluna')}
                                />
                            </LabelledSelect>
                        </Box>
                    )}

                    <Box>
                        <Typography
                            sx={{
                                fontSize: 15,
                                fontWeight: 700,
                                color: cc.textPrimary,
                                display: 'block',
                                mb: 1.25,
                            }}
                        >
                            {t('automacoes.quando')}
                        </Typography>

                        {/* Cinco opções não cabem numa linha sem espremer o texto: quebram em duas colunas. */}
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
                                gap: 1.25,
                            }}
                        >
                            {[
                                { value: EStageTrigger.OnEnter, label: t('automacoes.aoEntrar') },
                                {
                                    value: EStageTrigger.AfterDaysIdle,
                                    label: t('automacoes.aposDiasParado'),
                                },
                                {
                                    value: EStageTrigger.AfterDaysNoReply,
                                    label: t('automacoes.aposDiasSemResposta'),
                                },
                                { value: EStageTrigger.OnWon, label: t('automacoes.aoGanhar') },
                                { value: EStageTrigger.OnLost, label: t('automacoes.aoPerder') },
                                {
                                    value: EStageTrigger.BeforeSchedule,
                                    label: t('automacoes.antesDoAgendamento'),
                                },
                                {
                                    value: EStageTrigger.AfterScheduleDone,
                                    label: t('automacoes.agendamentoConcluido'),
                                },
                            ].map((option) => {
                                const active = trigger === option.value;

                                return (
                                    <Box
                                        key={option.value}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => setTrigger(option.value)}
                                        onKeyDown={(e) =>
                                            e.key === 'Enter' && setTrigger(option.value)
                                        }
                                        sx={{
                                            px: 2,
                                            py: 1.4,
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            fontSize: 14,
                                            fontWeight: active ? 700 : 500,
                                            textAlign: 'center',
                                            color: active ? cc.onTint : cc.textSecondary,
                                            border: `1px solid ${active ? cc.accent : cc.border}`,
                                            bgcolor: active ? cc.accentSoftBg : 'transparent',
                                        }}
                                    >
                                        {option.label}
                                    </Box>
                                );
                            })}
                        </Box>

                        {trigger === EStageTrigger.BeforeSchedule && (
                            <>
                                <TextField
                                    label={t('automacoes.horasAntes')}
                                    type="number"
                                    value={hoursBefore}
                                    onChange={(e) => setHoursBefore(Number(e.target.value))}
                                    sx={{ ...field, mt: 1.5, width: 210 }}
                                />
                                <Typography
                                    variant="caption"
                                    sx={{ color: cc.textSecondary, display: 'block', mt: 0.75 }}
                                >
                                    {t('automacoes.horasAntesAjuda')}
                                </Typography>
                            </>
                        )}

                        {needsDueDate && (
                            <>
                                <TextField
                                    label={
                                        trigger === EStageTrigger.AfterDaysNoReply
                                            ? t('automacoes.diasSemResposta')
                                            : t('automacoes.diasParado')
                                    }
                                    type="number"
                                    value={days}
                                    onChange={(e) => setDays(Number(e.target.value))}
                                    sx={{ ...field, mt: 1.5, width: 210 }}
                                />

                                {/* A diferença entre os gatilhos não é óbvia pelo nome, e errar gera regra que não dispara. */}
                                <Typography
                                    variant="caption"
                                    sx={{ color: cc.textSecondary, display: 'block', mt: 0.75 }}
                                >
                                    {trigger === EStageTrigger.AfterDaysNoReply
                                        ? t('automacoes.semRespostaAjuda')
                                        : t('automacoes.paradoAjuda')}
                                </Typography>
                            </>
                        )}
                    </Box>

                    <Box>
                        <Typography
                            sx={{
                                fontSize: 15,
                                fontWeight: 700,
                                color: cc.textPrimary,
                                display: 'block',
                                mb: 0.5,
                            }}
                        >
                            {t('automacoes.filtros')}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{ color: cc.textMuted, display: 'block', mb: 1.25 }}
                        >
                            {t('automacoes.filtrosAjuda')}
                        </Typography>

                        {/* Rótulo em cima e ajuda embaixo: o react-select não tem label flutuante. */}
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1.5,
                                flexWrap: 'wrap',
                                alignItems: 'flex-start',
                            }}
                        >
                            {/* Medium e mesma ajuda do select ao lado: o small padrão fica mais baixo que os 43px do select. */}
                            <Box sx={{ width: 250 }}>
                                <LabelledSelect cc={cc} label={t('automacoes.valorMinimo')}>
                                    <CurrencyInput
                                        value={minValue}
                                        onChange={setMinValue}
                                        fullWidth
                                        size="medium"
                                        sx={field}
                                    />
                                    <Typography
                                        variant="caption"
                                        sx={{ color: cc.textMuted, display: 'block', mt: 0.5 }}
                                    >
                                        {t('automacoes.condicaoAjuda')}
                                    </Typography>
                                </LabelledSelect>
                            </Box>

                            <Box sx={{ width: 250 }}>
                                <LabelledSelect cc={cc} label={t('automacoes.origemLead')}>
                                    <StandaloneAutoCompleteSelect
                                        options={SOURCES.map((item) => ({
                                            value: item.value,
                                            label: t(item.key),
                                        }))}
                                        value={source}
                                        onChange={(value) =>
                                            setSource(
                                                value === null || value === '' ? '' : Number(value)
                                            )
                                        }
                                        placeholder={t('automacoes.qualquerOrigem')}
                                        isClearable
                                    />
                                    <Typography
                                        variant="caption"
                                        sx={{ color: cc.textMuted, display: 'block', mt: 0.5 }}
                                    >
                                        {t('automacoes.origemAjuda')}
                                    </Typography>
                                </LabelledSelect>
                            </Box>
                        </Box>
                    </Box>
                </>
            )}

            {step === 1 && (
                <>
                    <Box>
                        <Typography
                            sx={{
                                fontSize: 15,
                                fontWeight: 700,
                                color: cc.textPrimary,
                                display: 'block',
                                mb: 1.25,
                            }}
                        >
                            {t('automacoes.entao')}
                        </Typography>

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
                                gap: 1.25,
                            }}
                        >
                            {actions.map((option) => {
                                const active = action === option.value;

                                return (
                                    <Box
                                        key={option.value}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => setAction(option.value)}
                                        onKeyDown={(e) =>
                                            e.key === 'Enter' && setAction(option.value)
                                        }
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.25,
                                            px: 1.75,
                                            py: 1.4,
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            fontSize: 14,
                                            fontWeight: active ? 700 : 500,
                                            color: active ? cc.onTint : cc.textSecondary,
                                            border: `1px solid ${active ? cc.accent : cc.border}`,
                                            bgcolor: active ? cc.accentSoftBg : 'transparent',
                                            '& .MuiSvgIcon-root': {
                                                color: active ? cc.accent : cc.textSecondary,
                                            },
                                        }}
                                    >
                                        {option.icon}
                                        {option.label}
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>

                    {action === EStageAction.SendMessage && (
                        <MessageField
                            required
                            label={t('automacoes.mensagem')}
                            value={message}
                            onChange={setMessage}
                            error={Boolean(touched && !message.trim())}
                            helperText={
                                touched && !message.trim()
                                    ? t('automacoes.informeAMensagem')
                                    : t('automacoes.mensagemAjuda')
                            }
                            variableSize="large"
                            multiline
                            minRows={3}
                            fullWidth
                            sx={field}
                        />
                    )}

                    {action === EStageAction.CreateTask && (
                        // Chips fora da linha do prazo: dentro do campo, ficariam com a largura dele e quebrariam.
                        <Box>
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                                <TextField
                                    required
                                    inputRef={taskRef}
                                    label={t('automacoes.tarefa')}
                                    value={task}
                                    onChange={(e) => setTask(e.target.value)}
                                    error={Boolean(touched && !task.trim())}
                                    helperText={
                                        touched && !task.trim() ? t('automacoes.informeATarefa') : ' '
                                    }
                                    fullWidth
                                    sx={field}
                                />
                                <TextField
                                    label={t('automacoes.prazoEmDias')}
                                    type="number"
                                    value={dueInDays}
                                    onChange={(e) => setDueInDays(Number(e.target.value))}
                                    sx={{ ...field, width: 180, flexShrink: 0 }}
                                />
                            </Box>
                            <VariableChips
                                size="large"
                                onInsert={(key) => setTask(insertAtCursor(taskRef.current, task, key))}
                            />
                        </Box>
                    )}

                    {action === EStageAction.Assign && (
                        <LabelledSelect cc={cc} label={t('automacoes.responsavel')}>
                            <StandaloneAutoCompleteSelect
                                options={[
                                    { value: '', label: t('automacoes.quemTemMenos') },
                                    ...users.map((user) => ({
                                        value: user.id,
                                        label: user.name,
                                    })),
                                ]}
                                value={assignee}
                                onChange={(value) => setAssignee((value as string) ?? '')}
                                placeholder={t('automacoes.quemTemMenos')}
                            />
                        </LabelledSelect>
                    )}

                    {action === EStageAction.MoveToColumn && (
                        <LabelledSelect cc={cc} label={t('automacoes.destino')}>
                            <StandaloneAutoCompleteSelect
                                options={columns
                                    .filter((item) => item.id !== column)
                                    .map((item) => ({ value: item.id, label: item.name }))}
                                value={destination}
                                onChange={(value) => setDestination((value as string) ?? '')}
                                placeholder={t('general.selecione')}
                            />
                        </LabelledSelect>
                    )}

                    {action === EStageAction.SetStatus && (
                        <LabelledSelect cc={cc} label={t('automacoes.statusAlvo')}>
                            <StandaloneAutoCompleteSelect
                                options={statuses.map((item) => ({
                                    value: item.id,
                                    label: item.name,
                                }))}
                                value={status}
                                onChange={(value) => setStatus((value as string) ?? '')}
                                placeholder={t('general.selecione')}
                            />
                        </LabelledSelect>
                    )}

                    {/* A regra não escolhe dono: lead sem responsável gera tarefa que ninguém vê na agenda. */}
                    {action === EStageAction.CreateTask && (
                        <Typography
                            variant="caption"
                            sx={{ color: 'warning.main', display: 'block', mt: -1 }}
                        >
                            {t('automacoes.tarefaSemDono')}
                        </Typography>
                    )}

                    <Box sx={{ width: 230 }}>
                        <LabelledSelect cc={cc} label={t('automacoes.atraso')}>
                            <TextField
                                type="number"
                                value={delayHours}
                                onChange={(e) => setDelayHours(Number(e.target.value))}
                                fullWidth
                                helperText={t('automacoes.atrasoAjuda')}
                                sx={field}
                            />
                        </LabelledSelect>
                    </Box>

                    {/* Obrigatório marcado no campo, não só na mensagem: descobrir ao salvar demora mais. */}
                    <LabelledSelect cc={cc} label={`${t('automacoes.nomeDaRegra')} *`}>
                        <TextField
                            required
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            fullWidth
                            error={Boolean(touched && !name.trim())}
                            helperText={
                                touched && !name.trim() ? t('automacoes.informeONome') : ' '
                            }
                            inputProps={{ 'aria-label': t('automacoes.nomeDaRegra') }}
                            sx={field}
                        />
                    </LabelledSelect>
                </>
            )}
        </RulePanel>
    );
};
